import {
  Component,
  signal,
  ChangeDetectionStrategy,
  inject,
  computed,
  effect,
} from '@angular/core';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { NgOptimizedImage } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { FormStatusService } from '../../service/form-status.service';
import { MenuService } from '../../service/menu.service';
import { AuthService } from '../../../core/service/auth.service';
import { OrganizationService } from '../../service/api/organization.service';
import { BadgeInfo, SubsectionBadgeInfo } from '../../models/models';
import {
  getStatusBadgeInfo,
  getStatusPillClass,
  getStatusTooltipPillClass,
} from '../../utils/status-pill.utils';
import { SECTION_STATUS_KEY_MAP } from '../../maps/section-status-key.map';
import { getSubsectionStatusLabel } from '../../maps/labels/status-labels.map';

@Component({
  selector: 'app-sidebar',
  imports: [RouterLink, RouterLinkActive, NgOptimizedImage],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Sidebar navigation for the dashboard area.
 *
 * The component keeps manual expansion state for collapsible menu groups and
 * auto-opens the group that owns the current route on first load or direct navigation.
 * After that synchronization, users remain in control of expanding or collapsing groups.
 */
export class Sidebar {
  private router = inject(Router);
  private menuService = inject(MenuService);
  private formStatusService = inject(FormStatusService);
  private authService = inject(AuthService);
  private organizationService = inject(OrganizationService);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  isCollapsed = signal(false);
  expandedMenus = signal<Set<string>>(new Set(['base-info']));

  readonly isAdmin = this.authService.isAdmin;
  readonly status = this.organizationService.status;
  readonly statusPillClass = getStatusPillClass;
  readonly tooltipStatusPillClass = getStatusTooltipPillClass;

  readonly menuItems = computed(() => {
    const items = this.menuService.menuItems;

    return items;
  });

  /**
   * Auto-expands the parent menu when the current route matches one of its child routes.
   * This only adds missing expanded items and never collapses menus automatically.
   */
  constructor() {
    effect(() => {
      const currentUrl = this.currentUrl();
      const matchingMenuIds = this.menuItems()
        .filter((item) =>
          item.children?.some((child) => child.route != null && currentUrl.startsWith(child.route)),
        )
        .map((item) => item.id);

      if (matchingMenuIds.length === 0) {
        return;
      }

      this.expandedMenus.update((set) => {
        const nextSet = new Set(set);
        let changed = false;

        for (const menuId of matchingMenuIds) {
          if (!nextSet.has(menuId)) {
            nextSet.add(menuId);
            changed = true;
          }
        }

        return changed ? nextSet : set;
      });
    });
  }

  /** Toggles the compact sidebar state. */
  toggleSidebar(): void {
    this.isCollapsed.update((v) => !v);
  }

  /** Toggles a collapsible menu group opened or closed. */
  toggleMenu(menuId: string): void {
    this.expandedMenus.update((set) => {
      const newSet = new Set(set);
      if (newSet.has(menuId)) {
        newSet.delete(menuId);
      } else {
        newSet.add(menuId);
      }
      return newSet;
    });
  }

  /** Returns whether a given menu group is currently expanded. */
  isExpanded(menuId: string): boolean {
    return this.expandedMenus().has(menuId);
  }

  /** Returns the badge information for a top-level menu item. */
  getBadgeInfo(id: string) {
    return this.formStatusService.getBadgeInfo(id)();
  }

  /**
   * Returns the badge for a child menu item:
   * - Active item (loaded): shows form-completion badge (e.g. "4/4 Obbligatori")
   * - Active item (loading): no badge shown (subsection badge handles status display)
   * - Inactive item without subsections: shows the section status from the API
   * - Inactive item with subsections: no badge (subsection badge shown separately)
   */
  getChildBadge(id: string, isActive: boolean): BadgeInfo | undefined {
    if (isActive) {
      return this.formStatusService.getBadgeInfo(id)() ?? undefined;
    }
    // Skip section_statuses badge for items that have subsections
    if (this.hasSubsections(id)) return undefined;
    return this.getSectionStatusBadge(id);
  }

  private hasSubsections(menuId: string): boolean {
    const sectionKey = SECTION_STATUS_KEY_MAP[menuId];
    if (!sectionKey || sectionKey === 'rentri') return false;
    const count = this.organizationService.status()?.subsection_counts?.[sectionKey] ?? 0;
    return count > 0;
  }

  private getSectionStatusBadge(menuId: string): BadgeInfo | undefined {
    const sectionKey = SECTION_STATUS_KEY_MAP[menuId];
    if (!sectionKey) return undefined;

    const sectionStatuses = this.organizationService.status()?.section_statuses;
    if (!sectionStatuses) return undefined;

    const status = sectionStatuses[sectionKey];
    if (!status) return undefined;

    return getStatusBadgeInfo(status, getSubsectionStatusLabel);
  }

  /**
   * Aggregates subsection statuses for a child item and builds the tooltip payload.
   */
  getSubsectionBadge(menuId: string): SubsectionBadgeInfo | undefined {
    const sectionKey = SECTION_STATUS_KEY_MAP[menuId];
    if (!sectionKey || sectionKey === 'rentri') return undefined;

    const orgStatus = this.organizationService.status();
    if (!orgStatus) return undefined;

    const subsections = orgStatus.subsection_statuses?.[sectionKey];
    const count = orgStatus.subsection_counts?.[sectionKey] ?? 0;
    if (!subsections || count === 0) return undefined;

    const entries = Object.entries(subsections);
    if (entries.length === 0) return undefined;

    const details = entries.map(([name, status]) => ({
      name,
      status: getSubsectionStatusLabel(status),
      statusKey: status,
    }));

    const statuses = entries.map(([, s]) => s);
    const unique = new Set(statuses);

    const allSame = unique.size === 1;
    const singleStatus = allSame ? statuses[0] : null;

    // All same non-draft status
    if (allSame) {
      const badge = getStatusBadgeInfo(singleStatus, getSubsectionStatusLabel);
      if (badge) {
        return { ...badge, count, details };
      }
    }

    // Any mix of statuses → attention
    return { text: 'Attenzione', variant: 'error', count, details };
  }
}
